<template>
  <div class="space-y-5">

    <!-- Summary Cards -->
    <div class="grid grid-cols-2 md:grid-cols-3 gap-3">
      <div class="bg-gray-900 rounded-xl p-4 border border-teal-800/40">
        <p class="text-xs text-teal-400 mb-1">Resgates Realizados</p>
        <p class="text-2xl font-bold text-white">{{ claims.length }}</p>
      </div>
      <div class="bg-gray-900 rounded-xl p-4 border border-green-800/40">
        <p class="text-xs text-green-400 mb-1">Lucro Total Resgatado</p>
        <p class="text-2xl font-bold text-green-400">${{ totalProfit.toFixed(2) }}</p>
      </div>
      <div class="bg-gray-900 rounded-xl p-4 border border-yellow-800/40">
        <p class="text-xs text-yellow-400 mb-1">Pendentes de Resgate</p>
        <p class="text-2xl font-bold text-yellow-400">{{ pending.length }}</p>
      </div>
    </div>

    <!-- Pendentes de resgate -->
    <div class="bg-gray-900 rounded-2xl border border-yellow-800/40 overflow-hidden">
      <div class="px-5 py-3 border-b border-gray-800 flex items-center justify-between">
        <div class="flex items-center gap-2">
          <span class="text-base">⏳</span>
          <h3 class="text-sm font-semibold text-white">Pendentes de Resgate</h3>
          <span v-if="pending.length > 0" class="bg-yellow-500/20 text-yellow-400 text-[10px] font-bold px-2 py-0.5 rounded-full animate-pulse">
            {{ pending.length }} pendente{{ pending.length > 1 ? 's' : '' }}
          </span>
        </div>
        <div class="flex items-center gap-2">
          <span v-if="pendingMsg" class="text-[11px]" :class="pendingOk ? 'text-teal-400' : 'text-orange-400'">{{ pendingMsg }}</span>
          <button @click="checkPending" :disabled="checkingPending"
            class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border"
            :class="checkingPending ? 'bg-gray-700 border-gray-600 text-gray-400 cursor-wait' : 'bg-blue-500/10 border-blue-500/30 text-blue-400 hover:bg-blue-500/20'">
            <span v-if="checkingPending" class="animate-spin">⟳</span>
            <span v-else>🔍</span>
            Verificar Pendentes
          </button>
          <button v-if="pending.length > 0" @click="redeemAll" :disabled="redeeming"
            class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border"
            :class="redeeming ? 'bg-gray-700 border-gray-600 text-gray-400 cursor-wait' : 'bg-yellow-500/10 border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/20'">
            <span v-if="redeeming" class="animate-spin">⟳</span>
            <span v-else>⚡</span>
            Resgatar Todos ({{ pending.length }})
          </button>
        </div>
      </div>

      <div v-if="!checkedPending" class="px-5 py-8 text-center text-gray-500 text-xs">
        Clique em <b class="text-blue-400">Verificar Pendentes</b> para buscar posições vencedoras prontas para resgate.
      </div>
      <div v-else-if="checkingPending" class="px-5 py-8 text-center text-gray-500 text-xs animate-pulse">
        Consultando Polymarket...
      </div>
      <div v-else-if="pending.length === 0" class="px-5 py-8 text-center text-gray-500 text-xs">
        <span class="text-2xl block mb-2">✅</span>
        Nenhuma posição pendente de resgate. Tudo em dia!
      </div>
      <div v-else class="divide-y divide-gray-800/50">
        <div v-for="pos in pending" :key="pos.id"
          class="flex items-center gap-3 px-5 py-3 hover:bg-yellow-500/5 transition">
          <div class="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-yellow-500/20">
            <span class="text-yellow-400 text-base">🏆</span>
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-[12px] font-medium text-white truncate">{{ pos.question || pos.market_id || '—' }}</p>
            <span v-if="pos.outcome" class="text-[10px] px-1.5 rounded font-bold bg-green-500/20 text-green-400 mt-0.5 inline-block">
              {{ pos.outcome }}
            </span>
          </div>
          <div class="text-right shrink-0">
            <p class="text-yellow-400 font-bold text-sm">${{ Number(pos.currentValue || pos.size || 0).toFixed(3) }}</p>
            <p class="text-[10px] text-gray-500">disponível</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Histórico de resgates realizados -->
    <div class="bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden">
      <div class="px-5 py-3 border-b border-gray-800 flex items-center justify-between">
        <div class="flex items-center gap-2">
          <span class="text-base">💰</span>
          <h3 class="text-sm font-semibold text-white">Histórico de Resgates</h3>
          <span v-if="claims.length > 0" class="bg-teal-500/20 text-teal-400 text-[10px] font-bold px-2 py-0.5 rounded-full">
            {{ claims.length }} registro{{ claims.length > 1 ? 's' : '' }}
          </span>
        </div>
        <div class="flex items-center gap-2">
          <span v-if="error" class="text-[11px] text-orange-400">{{ error }}</span>
          <span v-if="walletInfo" class="text-[10px] text-gray-600 font-mono">{{ walletInfo }}</span>
          <button @click="fetchClaims" :disabled="loading"
            class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all border"
            :class="loading ? 'bg-gray-700 border-gray-600 text-gray-400 cursor-wait' : 'bg-teal-500/10 border-teal-500/30 text-teal-400 hover:bg-teal-500/20'">
            <span v-if="loading" class="animate-spin">⟳</span>
            <span v-else>↻</span>
            Atualizar
          </button>
        </div>
      </div>

      <div v-if="loading" class="px-5 py-10 text-center text-gray-500 text-xs animate-pulse">Consultando Polymarket...</div>
      <div v-else-if="error && !claims.length" class="px-5 py-10 text-center text-gray-500 text-xs">
        <span class="text-3xl block mb-2">🔑</span>
        <p>{{ error }}</p>
        <p class="text-gray-600 mt-1">Configure sua carteira Polymarket nas Configurações.</p>
      </div>
      <div v-else-if="!loading && !claims.length" class="px-5 py-10 text-center text-gray-500 text-xs">
        <span class="text-3xl block mb-2">✅</span>
        <p>Nenhum resgate encontrado na Polymarket.</p>
      </div>
      <div v-else class="divide-y divide-gray-800/50">
        <div v-for="claim in pagedClaims" :key="claim.id"
          class="flex items-center gap-3 px-5 py-3 hover:bg-gray-800/30 transition">
          <div class="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-teal-500/20">
            <span class="text-teal-400 text-base">🏆</span>
          </div>
          <div class="flex-1 min-w-0">
            <p class="text-[12px] font-medium text-white truncate">{{ claim.question || claim.market_id || '—' }}</p>
            <div class="flex items-center gap-2 mt-0.5">
              <span v-if="claim.outcome" class="text-[10px] px-1.5 rounded font-bold bg-teal-500/20 text-teal-400">{{ claim.outcome }}</span>
              <span class="text-[10px] text-gray-500">{{ formatDate(claim.timestamp) }}</span>
            </div>
          </div>
          <div class="text-right shrink-0">
            <p class="text-green-400 font-bold text-sm">+${{ Number(claim.profit || 0).toFixed(3) }}</p>
            <a v-if="claim.tx" :href="`https://polygonscan.com/tx/${claim.tx}`" target="_blank"
              class="text-[10px] text-blue-500/60 hover:text-blue-400 font-mono transition">
              {{ claim.tx.substring(0, 10) }}…
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="flex justify-between items-center">
      <p class="text-xs text-gray-500">{{ totalFiltered > 0 ? (currentPage - 1) * pageSize + 1 : 0 }}–{{ Math.min(currentPage * pageSize, totalFiltered) }} de {{ totalFiltered }}</p>
      <div class="flex items-center gap-1.5">
        <button @click="currentPage--" :disabled="currentPage === 1" class="px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-xs font-medium hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed">‹ Anterior</button>
        <span class="px-3 py-1.5 text-xs text-gray-400 bg-gray-800/50 rounded-lg">{{ currentPage }} / {{ totalPages }}</span>
        <button @click="currentPage++" :disabled="currentPage >= totalPages" class="px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-xs font-medium hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed">Próxima ›</button>
      </div>
    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { getApiBaseUrl } from '../config/runtime';
import { useAuthStore } from '../stores/authStore';

const authStore = useAuthStore();

const claims = ref([]);
const loading = ref(false);
const error = ref('');
const walletInfo = ref('');
const currentPage = ref(1);
const pageSize = 20;

// Pending state
const pending = ref([]);
const checkingPending = ref(false);
const checkedPending = ref(false);
const redeeming = ref(false);
const pendingMsg = ref('');
const pendingOk = ref(true);

const totalProfit = computed(() => claims.value.reduce((s, c) => s + Number(c.profit || 0), 0));
const lastClaimDate = computed(() => {
  const sorted = [...claims.value].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  return sorted.length ? formatDate(sorted[0].timestamp) : 'Nenhum';
});

const totalFiltered = computed(() => claims.value.length);
const totalPages = computed(() => Math.max(1, Math.ceil(totalFiltered.value / pageSize)));
const pagedClaims = computed(() => claims.value.slice((currentPage.value - 1) * pageSize, currentPage.value * pageSize));

const formatDate = (d) => d ? new Date(d).toLocaleString('pt-BR') : '-';

function authHeader() {
  return authStore.token ? { Authorization: `Bearer ${authStore.token}` } : {};
}

async function fetchClaims() {
  if (!authStore.token) return;
  loading.value = true;
  error.value = '';
  walletInfo.value = '';
  currentPage.value = 1;
  try {
    const res = await fetch(`${getApiBaseUrl()}/v1/user/claims-live?limit=500`, { headers: authHeader() });
    if (res.ok) {
      const data = await res.json();
      if (data.error) error.value = data.error;
      if (data.wallet) walletInfo.value = data.wallet;
      claims.value = (data.claims || []).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    } else {
      error.value = 'Erro ao buscar resgates';
    }
  } catch (e) {
    error.value = 'Erro de conexão';
  } finally {
    loading.value = false;
  }
}

async function checkPending() {
  checkingPending.value = true;
  pendingMsg.value = '';
  checkedPending.value = true;
  try {
    const res = await fetch(`${getApiBaseUrl()}/v1/user/claims-pending`, { headers: authHeader() });
    if (res.ok) {
      const data = await res.json();
      if (data.error) { pendingMsg.value = data.error; pendingOk.value = false; }
      else {
        pending.value = data.pending || [];
        pendingMsg.value = pending.value.length > 0
          ? `${pending.value.length} posição(ões) para resgatar`
          : 'Nenhum pendente';
        pendingOk.value = true;
      }
    } else {
      pendingMsg.value = 'Erro ao verificar';
      pendingOk.value = false;
    }
  } catch {
    pendingMsg.value = 'Erro de conexão';
    pendingOk.value = false;
  } finally {
    checkingPending.value = false;
  }
}

async function redeemAll() {
  redeeming.value = true;
  pendingMsg.value = 'Enviando para o bot processar...';
  pendingOk.value = true;
  try {
    await fetch(`${getApiBaseUrl()}/v1/claims/force-scan`, {
      method: 'POST',
      headers: { ...authHeader(), 'Content-Type': 'application/json' },
    });
    pendingMsg.value = 'Bot iniciando resgates! Aguarde alguns minutos.';
    // Atualizar após 10s
    setTimeout(() => { checkPending(); fetchClaims(); }, 10000);
  } catch {
    pendingMsg.value = 'Erro ao acionar resgate';
    pendingOk.value = false;
  } finally {
    redeeming.value = false;
  }
}

onMounted(fetchClaims);
</script>
